import { useEffect, useRef, useState } from 'react';
import { motion, useInView, animate } from 'framer-motion';

type Stat = {
  to: number;
  decimals: number;
  prefix?: string;
  suffix: string;
  label: string;
};

const stats: Stat[] = [
  { to: 500, decimals: 0, suffix: '+', label: 'Brands scaled' },
  { to: 95, decimals: 0, suffix: '%', label: 'Client retention' },
  { to: 1, decimals: 0, suffix: 'M+', label: 'Posts optimized' },
  { to: 4.2, decimals: 1, suffix: 'x', label: 'Avg. ROAS lift' },
];

function CountUp({
  to,
  decimals,
  prefix = '',
  suffix,
  inView,
}: {
  to: number;
  decimals: number;
  prefix?: string;
  suffix: string;
  inView: boolean;
}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1], // out-expo: fast start, smooth landing
      onUpdate: (v) => setValue(v),
    });
    return controls.stop;
  }, [inView, to]);

  return (
    <span className="tabular-nums">
      {prefix}
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

export default function Stats() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section className="relative bg-background text-foreground py-20 md:py-28 px-8 md:px-28">
      <div className="max-w-6xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-border bg-card overflow-hidden"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border [&>div:nth-child(-n+2)]:border-b md:[&>div:nth-child(-n+2)]:border-b-0 [&>div:nth-child(-n+2)]:border-border">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="px-6 py-10 md:py-14 text-center"
              >
                <div className="text-4xl md:text-6xl font-medium tracking-[-1.5px]">
                  <CountUp
                    to={s.to}
                    decimals={s.decimals}
                    prefix={s.prefix}
                    suffix={s.suffix}
                    inView={inView}
                  />
                </div>
                <div className="mt-2 text-sm md:text-base text-muted-foreground">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
